/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import get from 'lodash/get';
import { Icon } from '../../ui/Icon';
import { types, arrows, sizes, arrowPositions, decorations } from './types';
import { Link } from '@sitecore-jss/sitecore-jss-react';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { processLinkData } from '../../../utils/analytics';
import {
  arrowCss,
  arrowDownCss,
  arrowLeftCss,
  arrowStartCss,
  arrowEndCss,
  regularFontCss,
  redUnderlineCss,
  greyUnderlineCss,
  redTextCss,
  greyTextCss,
  blackTextCss,
  smallFontCss,
  largeFontCss,
} from './index.styles';

class CtaLink extends SafePureComponent {
  state = {
    linkAttributes: {
      value: {
        href: this.props.href || '',
        text: this.props.text || '',
        target: this.props.target || '',
        rel: this.props.rel || '',
      },
    },
  };

  safeRender() {
    const {
      cta,
      type = types.primary,
      arrow = arrows.none,
      arrowPosition = arrowPositions.end,
      decoration = decorations.underline,
      size = sizes.regular,
      linkCustomCss,
      sitecoreContext,
      onClick = null,
      arrowName = 'arrow-right',
      className,
      isGTMTrackable,
      componentName,
    } = this.props;

    const { linkAttributes } = this.state;

    const isEditing = get(sitecoreContext, 'pageEditing', false);

    const typeCssMap = {
      [types.primary]: redTextCss,
      [types.secondary]: greyTextCss,
      [types.tertiary]: blackTextCss,
    };

    const sizeCssMap = {
      [sizes.small]: smallFontCss,
      [sizes.regular]: regularFontCss,
      [sizes.large]: largeFontCss,
    };

    const arrowCssVariation = {
      [arrows.none]: null,
      [arrows.right]: null,
      [arrows.down]: arrowDownCss,
      [arrows.left]: arrowLeftCss,
    };

    const getColor = typeCssMap[type];
    const getSize = sizeCssMap[size];
    const getArrowVariation = arrowCssVariation[arrow];
    const getArrowCss = [arrowCss, getArrowVariation];
    const getLinkCss = [getSize, getColor, linkCustomCss];
    const getUnderlineColorCss =
      type === types.primary ? redUnderlineCss : greyUnderlineCss;

    const shouldRenderUnderline = decoration === decorations.underline;
    const shouldRenderArrow = arrow !== arrows.none;
    const shouldRenderArrowStart =
      shouldRenderArrow && arrowPosition === arrowPositions.start;
    const shouldRenderArrowEnd =
      shouldRenderArrow && arrowPosition === arrowPositions.end;

    const fieldCta = cta || linkAttributes;

    if (isEditing) {
      return (
        <React.Fragment>
          {shouldRenderArrowStart && (
            <Icon
              customCss={[getArrowCss, getColor, arrowStartCss]}
              name={arrowName}
            />
          )}
          <Link
            css={[getLinkCss, shouldRenderUnderline && getUnderlineColorCss]}
            field={fieldCta}
          />
          {shouldRenderArrowEnd && (
            <Icon
              customCss={[getArrowCss, getColor, arrowEndCss]}
              name={arrowName}
            />
          )}
        </React.Fragment>
      );
    }

    return (
      <Link
        onClick={e => {
          e.preventDefault();
          processLinkData(componentName, fieldCta, isGTMTrackable, onClick);
          return true;
        }}
        css={[getLinkCss, shouldRenderUnderline && getUnderlineColorCss]}
        field={fieldCta}
        className={className}
      >
        {shouldRenderArrowStart && (
          <Icon customCss={[getArrowCss, arrowStartCss]} name={arrowName} />
        )}
        <span>{get(fieldCta, 'value.text')}</span>
        {shouldRenderArrowEnd && (
          <Icon customCss={[getArrowCss, arrowEndCss]} name={arrowName} />
        )}
      </Link>
    );
  }
}

CtaLink.displayName = 'CtaLink';

CtaLink.propTypes = {
  cta: PropTypes.shape({
    value: PropTypes.shape({
      href: PropTypes.string,
      linktype: PropTypes.string,
      text: PropTypes.string,
      anchor: PropTypes.string,
      querystring: PropTypes.string,
      target: PropTypes.string,
      id: PropTypes.string,
      rel: PropTypes.string,
    }),
  }),
  href: PropTypes.string,
  text: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  type: PropTypes.oneOf([types.primary, types.secondary, types.tertiary]),
  arrow: PropTypes.oneOf([arrows.none, arrows.right, arrows.down, arrows.left]),
  size: PropTypes.oneOf([sizes.regular, sizes.small, sizes.large]),
  linkCustomCss: PropTypes.object,
};

export default withSitecoreContext()(CtaLink);
